import React from 'react';

export type ResponsiveToolbarButtonVariant = 'primary' | 'secondary';

export interface ResponsiveToolbarButtonProps {
  /** Material Symbols icon name (e.g. arrow_back, history). */
  icon: string;
  /** Visible label on md+ screens. */
  label: string;
  onClick: () => void;
  variant?: ResponsiveToolbarButtonVariant;
  /** Defaults to label; used for aria-label on icon-only mobile button. */
  mobileAriaLabel?: string;
  disabled?: boolean;
  className?: string;
}

const styles = {
  primary: {
    desktop:
      'hidden md:flex h-9 shrink-0 items-center justify-center gap-2 rounded-full bg-primary text-white px-5 text-sm font-semibold shadow-md hover:opacity-90 disabled:opacity-50',
    mobile:
      'md:hidden flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-md hover:opacity-90 disabled:opacity-50',
  },
  secondary: {
    desktop:
      'hidden md:flex h-9 shrink-0 items-center justify-center gap-2 rounded-full bg-elevation-dark border border-border-dark px-5 text-sm font-medium text-slate-300 hover:bg-white/5 disabled:opacity-50',
    mobile:
      'md:hidden flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-elevation-dark border border-border-dark text-slate-300 hover:bg-white/5 disabled:opacity-50',
  },
} as const;

/**
 * Toolbar actions: icon + label from md breakpoint up; icon-only circle below md (matches HeaderPrimaryAddButton).
 */
export const ResponsiveToolbarButton: React.FC<ResponsiveToolbarButtonProps> = ({
  icon,
  label,
  onClick,
  variant = 'secondary',
  mobileAriaLabel,
  disabled,
  className = '',
}) => {
  const aria = mobileAriaLabel ?? label;
  const { desktop, mobile } = styles[variant];
  const extra = className.trim();

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`${desktop}${extra ? ` ${extra}` : ''}`}
      >
        <span className="material-symbols-outlined text-sm">{icon}</span>
        <span>{label}</span>
      </button>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`${mobile}${extra ? ` ${extra}` : ''}`}
        aria-label={aria}
        title={aria}
      >
        <span className="material-symbols-outlined text-sm">{icon}</span>
      </button>
    </>
  );
};
